import { encryptedBackupEnvelopeSchema, type EncryptedBackupEnvelope } from '@wolhasudang/shared';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export type EncryptedBackup = EncryptedBackupEnvelope;

export interface BackupBundle {
  recoveryCode: string;
  accessToken: string;
  envelope: EncryptedBackup;
}

export interface CloudBackupCredentials {
  lookupKey: string;
  accessToken: string;
  salt: string;
}

export function parseEncryptedBackup(value: unknown): EncryptedBackup {
  const parsed = encryptedBackupEnvelopeSchema.safeParse(value);
  if (!parsed.success) throw new Error('INVALID_BACKUP_FILE');
  return parsed.data;
}

export async function encryptBackup(plainJson: string): Promise<BackupBundle> {
  const secret = crypto.getRandomValues(new Uint8Array(32));
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const { encryptionKey, lookupKey, accessToken } = await derive(secret, salt);
  const compressed = await transform(encoder.encode(plainJson), 'compress');
  const aad = encoder.encode(`1|${lookupKey}`);
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: toBuffer(nonce), additionalData: toBuffer(aad) }, encryptionKey, toBuffer(compressed)));
  const checksum = toHex(new Uint8Array(await crypto.subtle.digest('SHA-256', toBuffer(ciphertext))));
  return {
    recoveryCode: `w1.${encode(secret)}.${encode(salt)}`,
    accessToken,
    envelope: { schemaVersion: 1, salt: encode(salt), lookupKey, nonce: encode(nonce), ciphertext: encode(ciphertext), checksum },
  };
}

export async function decryptBackup(envelope: EncryptedBackup, recoveryCode: string): Promise<string> {
  if (envelope.schemaVersion !== 1) throw new Error('UNSUPPORTED_BACKUP_SCHEMA');
  const ciphertext = decode(envelope.ciphertext);
  const checksum = toHex(new Uint8Array(await crypto.subtle.digest('SHA-256', toBuffer(ciphertext))));
  if (checksum !== envelope.checksum) throw new Error('BACKUP_CHECKSUM_MISMATCH');
  const recovered = parseRecoveryCode(recoveryCode, envelope.salt);
  const { encryptionKey, lookupKey } = await derive(recovered.secret, recovered.salt);
  if (lookupKey !== envelope.lookupKey) throw new Error('INVALID_RECOVERY_CODE');
  try {
    const compressed = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: toBuffer(decode(envelope.nonce)), additionalData: toBuffer(encoder.encode(`1|${lookupKey}`)) }, encryptionKey, toBuffer(ciphertext)));
    return decoder.decode(await transform(compressed, 'decompress'));
  } catch {
    throw new Error('INVALID_RECOVERY_CODE');
  }
}

export async function deriveCloudBackupCredentials(recoveryCode: string): Promise<CloudBackupCredentials> {
  const recovered = parseRecoveryCode(recoveryCode);
  const { lookupKey, accessToken } = await derive(recovered.secret, recovered.salt);
  return { lookupKey, accessToken, salt: encode(recovered.salt) };
}

export function encryptedBackupSize(envelope: EncryptedBackup): number {
  return decode(envelope.ciphertext).byteLength;
}

async function derive(secret: Uint8Array, salt: Uint8Array) {
  const source = await crypto.subtle.importKey('raw', toBuffer(secret), 'HKDF', false, ['deriveBits', 'deriveKey']);
  const bits = async (info: string) => new Uint8Array(await crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: toBuffer(salt), info: toBuffer(encoder.encode(info)) }, source, 256));
  const encryptionKey = await crypto.subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: toBuffer(salt), info: toBuffer(encoder.encode('wolhasudang/backup/encryption/v1')) }, source, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  return {
    encryptionKey,
    lookupKey: encode(await bits('wolhasudang/backup/lookup/v1')),
    accessToken: encode(await bits('wolhasudang/backup/access/v1')),
  };
}

async function transform(input: Uint8Array, mode: 'compress' | 'decompress'): Promise<Uint8Array> {
  const stream = mode === 'compress' ? new CompressionStream('gzip') : new DecompressionStream('gzip');
  // Consume while writing: browsers apply backpressure before write/close resolve.
  return new Uint8Array(await new Response(new Blob([toBuffer(input)]).stream().pipeThrough(stream)).arrayBuffer());
}

function encode(value: Uint8Array): string {
  let binary = '';
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function decode(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function parseRecoveryCode(value: string, envelopeSalt?: string): { secret: Uint8Array; salt: Uint8Array } {
  const normalized = value.trim();
  if (normalized.startsWith('w1.')) {
    const [version, secretValue, saltValue, extra] = normalized.split('.');
    if (version !== 'w1' || !secretValue || !saltValue || extra) throw new Error('INVALID_RECOVERY_CODE');
    try {
      const secret = decode(secretValue); const salt = decode(saltValue);
      if (secret.byteLength !== 32 || salt.byteLength !== 16 || (envelopeSalt && encode(salt) !== envelopeSalt)) throw new Error('INVALID_RECOVERY_CODE');
      return { secret, salt };
    } catch { throw new Error('INVALID_RECOVERY_CODE'); }
  }
  if (!envelopeSalt) throw new Error('RECOVERY_CODE_REQUIRES_BACKUP_FILE');
  try {
    const secret = decode(normalized); const salt = decode(envelopeSalt);
    if (secret.byteLength !== 32 || salt.byteLength !== 16) throw new Error('INVALID_RECOVERY_CODE');
    return { secret, salt };
  } catch { throw new Error('INVALID_RECOVERY_CODE'); }
}

function toHex(value: Uint8Array): string {
  return [...value].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function toBuffer(value: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(value.byteLength);
  copy.set(value);
  return copy.buffer;
}
